import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { useStore } from "@hooks/useZustandProvider";

export const MousePositionContext = createContext(null);

export const MousePositionProvider = ({ children }) => {
  const [data, setData] = useState({ x: 0, y: 0 });
  const lastDataRef = useRef(data);
  const { cms } = useStore();

  const { isEditMode } = cms.uiOptions;

  const update = useCallback((e) => {
    const withoutbubbling = Boolean(
      e.target.closest("[data-cursor-notice]")?.dataset?.withoutbubbling,
    );
    const blockIndex =
      e.target.closest("[data-block-index]")?.dataset?.blockIndex;
    const isWidget =
      e.target.closest("[data-is-widget]")?.dataset?.isWidget === "true";

    const parentCursorNotice = e.target.closest("[data-cursor-notice]")?.dataset
      ?.cursorNotice;

    const text =
      e.target.dataset?.cursorNotice ||
      (withoutbubbling ? "" : parentCursorNotice);

    if (
      !text &&
      lastDataRef.current.text === text &&
      lastDataRef.current.blockIndex === blockIndex &&
      lastDataRef.current.isWidget === isWidget
    ) {
      return;
    }

    const newData = {
      x: text ? e.x : lastDataRef.current.x,
      y: text ? e.y : lastDataRef.current.y,
      isTop: text ? e.view.innerHeight / 2 > e.y : lastDataRef.current.isTop,
      isLeft: text ? e.view.innerWidth / 2 > e.x : lastDataRef.current.isLeft,
      blockIndex,
      isWidget,
      text,
    };

    if (!shallowEqual(lastDataRef.current, newData)) {
      setData(newData);
      lastDataRef.current = newData;
    }
  }, []);

  useEffect(() => {
    if (isEditMode) {
      window.addEventListener("mousemove", update);
      window.addEventListener("touchmove", update);
      window.addEventListener("click", update);
    }

    return () => {
      window.removeEventListener("mousemove", update);
      window.removeEventListener("touchmove", update);
      window.removeEventListener("click", update);
    };
  }, [setData, isEditMode]);

  const context = useMemo(() => ({ ...data }), [data]);

  return (
    <MousePositionContext.Provider value={context}>
      {children}
    </MousePositionContext.Provider>
  );
};

export const useMousePosition = () => {
  const context = useContext(MousePositionContext);

  if (!context) {
    throw new Error(
      "[MousePositionProvider] Could not find dialog context provider.",
    );
  }
  return context;
};

function shallowEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (object1[key] !== object2[key]) {
      return false;
    }
  }

  return true;
}
